import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import api from '../services/api';
import {
  AlertCircleIcon,
  LockIcon,
  CheckIcon
} from '../components/Icons';
import './Auth.css';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { getLocalizedPath } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string'
        ? errorDetail
        : errorDetail?.message || t('forgotPassword.errors.generic');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Header */}
      <header className="auth-header">
        <div className="auth-header-content">
          <div className="auth-logo" onClick={() => navigate(getLocalizedPath('/'))}>
            <img src="/logo_transp.png" alt="ContratoSeguro" />
          </div>
          <div className="auth-header-actions">
            <button onClick={() => navigate(getLocalizedPath('/login'))} className="btn-text">
              {t('forgotPassword.backToLogin')}
            </button>
            <button onClick={() => navigate(getLocalizedPath('/signup'))} className="btn-primary-auth">
              {t('login.createAccount')}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="auth-main">
        <div className="auth-container">
          <div className="auth-grid">
            {/* Left Column - Form */}
            <div className="auth-form-column">
              <div className="auth-card">
                <div className="auth-card-header">
                  <h1>{t('forgotPassword.title')}</h1>
                  <p>{t('forgotPassword.subtitle')}</p>
                </div>

                {error && (
                  <div className="auth-error">
                    <AlertCircleIcon size={20} />
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="auth-success">
                    <CheckIcon size={20} />
                    <div>
                      <strong>{t('forgotPassword.success.title')}</strong>
                      <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                        {t('forgotPassword.success.message')}
                      </p>
                    </div>
                  </div>
                )}

                {!success ? (
                  <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-field">
                      <label htmlFor="email">{t('forgotPassword.email')}</label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('forgotPassword.emailPlaceholder')}
                        required
                        disabled={loading}
                        autoComplete="email"
                      />
                    </div>

                    <button type="submit" className="btn-primary-full" disabled={loading}>
                      {loading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
                    </button>

                    <div className="auth-secondary-action">
                      <a href={getLocalizedPath('/login')} className="link-secondary">{t('forgotPassword.backToLogin')}</a>
                    </div>
                  </form>
                ) : (
                  <div className="auth-form">
                    <button
                      onClick={() => navigate(getLocalizedPath('/login'))}
                      className="btn-primary-full"
                    >
                      {t('forgotPassword.backToLogin')}
                    </button>
                  </div>
                )}

                <div className="auth-trust-footer">
                  <LockIcon size={16} />
                  <span>{t('login.dataProtected')}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Presentation */}
            <div className="auth-presentation-column">
              <div className="auth-presentation">
                <h2>{t('forgotPassword.secureRecoveryTitle', { defaultValue: 'Secure password recovery' })}</h2>
                <p className="presentation-subtitle">
                  {t('forgotPassword.secureRecoverySubtitle', { defaultValue: 'We send a temporary link so you can create a new password safely.' })}
                </p>

                <div className="presentation-benefits">
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <LockIcon size={24} />
                    </div>
                    <div className="benefit-content">
                      <h3>{t('forgotPassword.benefits.linkTemporaryTitle', { defaultValue: 'Secure, temporary link' })}</h3>
                      <p>{t('forgotPassword.benefits.linkTemporaryDesc', { defaultValue: 'The reset link expires in 1 hour for your security' })}</p>
                    </div>
                  </div>

                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <CheckIcon size={24} />
                    </div>
                    <div className="benefit-content">
                      <h3>{t('forgotPassword.benefits.confirmationTitle', { defaultValue: 'Email confirmation' })}</h3>
                      <p>{t('forgotPassword.benefits.confirmationDesc', { defaultValue: 'You will receive a confirmation when your password is changed' })}</p>
                    </div>
                  </div>

                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <AlertCircleIcon size={24} />
                    </div>
                    <div className="benefit-content">
                      <h3>{t('forgotPassword.benefits.privacyTitle', { defaultValue: 'Privacy protection' })}</h3>
                      <p>{t('forgotPassword.benefits.privacyDesc', { defaultValue: 'We do not reveal whether an email is registered' })}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
